<script lang="ts">
  import { _ } from 'svelte-i18n';
  import type { ApplicationMode } from '$lib/stores/mode';
  import { applicationMode } from '$lib/stores/mode';
  import { queryResult, queryFilters } from '$lib/stores/selection';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { customerList } from '$lib/api/lists';
  import { modeSwitchDialog } from '$lib/stores/ui';
  import { resetSelection } from '$lib/stores/reset';
  import { aiModeActive } from '$lib/stores/ai';
  import { goto } from '$app/navigation';
  import { track } from '$lib/analytics/tracking';
  import { telemetry } from '$lib';

  export let selectedMode = $applicationMode;

  /**
   * Which tab reads as current, and none while the assistant is up.
   *
   * The assistant had a tab of its own here and no longer does — see `modes`
   * below. It is still reachable, so it still has to be possible for no tab to
   * be the answer: highlighting "Klassisch" underneath it would say the
   * customer is somewhere they are not.
   */
  $: visualMode = $aiModeActive ? null : selectedMode;

  let pendingMode: ApplicationMode | null = null;

  /**
   * The searches this switches between.
   *
   * The assistant was a third tab, and it was never a third mode: it resolved
   * to classic plus an overlay, so picking it changed the screen without
   * changing what the switcher claimed to be switching. It is reached from the
   * guide, which is where a customer describing what they want in prose is
   * already standing.
   */
  const modes = [
    { id: 'classic', label: 'Classic' },
    { id: 'neighbor', label: 'Nearest Neighbor' }
  ];

  // Check if switching between modes that have data
  const needsConfirmation = (fromMode: ApplicationMode, toMode: ApplicationMode) => {
    const dataModePairs = [
      ['classic', 'neighbor'],
      ['neighbor', 'classic']
    ];

    return dataModePairs.some((pair) => pair[0] === fromMode && pair[1] === toMode);
  };

  const hasDataOrFilters = () => {
    // Check if there are active filters or results
    const filters = $queryFilters;
    const hasFilters =
      filters.postal_codes?.length > 0 ||
      filters.cantons?.length > 0 ||
      filters.languages?.length > 0 ||
      filters.contact_roles?.length > 0 ||
      filters.postal_radius?.center_postal_code ||
      filters.company_size_range?.some((v) => v !== null) ||
      filters.founding_year_range?.some((v) => v !== null) ||
      filters.structured_filters?.legal_form?.length > 0 ||
      filters.structured_filters?.capital ||
      filters.structured_filters?.has_website ||
      filters.structured_filters?.has_email ||
      filters.structured_filters?.has_phone ||
      filters.structured_filters?.has_facebook ||
      filters.structured_filters?.has_linkedin ||
      filters.structured_filters?.has_instagram ||
      filters.structured_filters?.has_twitter ||
      filters.signal_filters?.capital_raised ||
      (filters.signal_filters?.selected?.length ?? 0) > 0 ||
      filters.embedding_query ||
      filters.text_constraints?.length > 0;

    const hasResults = $queryResult && $queryResult.length > 0;
    const hasSelectedPools = $selectedIndustryCentroids.size > 0;

    // An uploaded list counts as work, and now more than any of the above: a
    // reset deletes it, and it took a file and minutes of matching to make,
    // where every filter here is a click to set again. Without this a customer
    // who had only uploaded a list — which is the whole of the list door's
    // first step — was switched away from it with no question asked.
    const hasList = $customerList !== null;

    return hasFilters || hasResults || hasSelectedPools || hasList;
  };

  const selectMode = (mode: ApplicationMode) => {
    // Clicking classic while the assistant is up just closes it. Below `lg`
    // this bar is the only switcher on screen, so it is the way out of a
    // conversation on the width where the sidebar is a drawer.
    if (mode === 'classic' && $aiModeActive) {
      aiModeActive.set(false);
      return;
    }

    if (mode !== selectedMode) {
      // Deactivate AI overlay when switching to a real mode
      aiModeActive.set(false);

      // Check if we need confirmation
      const effectiveFrom = selectedMode;
      if (needsConfirmation(effectiveFrom, mode) && hasDataOrFilters()) {
        pendingMode = mode;
        modeSwitchDialog.set({
          show: true,
          fromMode: effectiveFrom,
          toMode: mode,
          onConfirm: confirmModeSwitch,
          onCancel: cancelModeSwitch
        });
      } else {
        // Direct switch for modes that don't need confirmation
        const fromMode = effectiveFrom;
        const toMode = mode;

        selectedMode = mode;
        goto(`/?mode=${mode}`);
        applicationMode.set(mode);

        // Track mode switch and pageView
        telemetry.pageView(`/?mode=${mode}`);
        track.modeSwitched({
          fromMode: fromMode,
          toMode: toMode,
          hadData: false
        });

        // A new mode is a new selection.
        //
        // This used to clear five stores, and only when moving between classic
        // and neighbour: the filters themselves survived, so a canton chosen in
        // one mode narrowed a search in the other from a panel that was not
        // showing it. resetSelection clears the lot, in one place that every
        // other reset also goes through.
        resetSelection();
      }
    }
  };

  const confirmModeSwitch = () => {
    if (pendingMode) {
      const fromMode = selectedMode;
      const toMode = pendingMode;
      const hadData = hasDataOrFilters();

      // The customer has just been asked whether to throw the selection away
      // and said yes. resetSelection is that, and preserves the contact mode
      // exactly as the literal this replaces did.
      resetSelection();

      // Track mode switch
      track.modeSwitched({
        fromMode: fromMode,
        toMode: toMode,
        hadData: hadData
      });

      // Switch mode and update URL
      selectedMode = pendingMode;
      goto(`/?mode=${pendingMode}`);
      applicationMode.set(pendingMode);
      aiModeActive.set(false);

      // Track pageView for the new mode
      telemetry.pageView(`/?mode=${pendingMode}`);

      // Reset dialog
      pendingMode = null;
      modeSwitchDialog.set({
        show: false,
        fromMode: '',
        toMode: '',
        onConfirm: null,
        onCancel: null
      });
    }
  };

  const cancelModeSwitch = () => {
    pendingMode = null;
    modeSwitchDialog.set({
      show: false,
      fromMode: '',
      toMode: '',
      onConfirm: null,
      onCancel: null
    });
  };
</script>

<div class="flex">
  <!-- Mobile dropdown -->
  <div class="sm:hidden">
    <select
      aria-label={$_('modes.switcherLabel')}
      class="mobile-mode-select w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 pr-7 text-xs font-medium text-gray-700 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
      value={visualMode}
      on:change={(e) => selectMode((e.currentTarget as HTMLSelectElement).value as ApplicationMode)}
    >
      {#each modes as mode}
        <option value={mode.id}>
          {mode.id === 'classic' ? 'Classic' : 'Neighbor'}
        </option>
      {/each}
    </select>
  </div>

  <!-- Desktop tabs -->
  <div class="hidden w-full sm:block">
    <nav
      class="switcher-nav flex w-full rounded-control border border-hairline bg-hairline p-1"
      aria-label={$_('modes.switcherLabel')}
    >
      {#each modes as mode}
        <button
          type="button"
          on:click={() => selectMode(mode.id as ApplicationMode)}
          class="switcher-tab flex-1 whitespace-nowrap rounded-lg px-2 py-1.5 text-xs font-medium transition-all duration-200 md:px-3 md:py-2 {visualMode ===
          mode.id
            ? 'switcher-tab-active bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/80'
            : 'text-gray-500 hover:text-gray-700'}"
        >
          <span class="hidden md:inline">{$_(`modes.${mode.id}`)}</span>
          <span class="md:hidden">
            {mode.id === 'classic' ? 'Classic' : 'Neighbor'}
          </span>
        </button>
      {/each}
    </nav>
  </div>
</div>

<style>
  /* Hide the default dropdown arrow across all browsers */
  .mobile-mode-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1em 1em;
  }

  /* For Firefox specifically */
  .mobile-mode-select::-ms-expand {
    display: none;
  }
</style>
